import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'

interface PasswordChangeFormProps {
  onPasswordChange: (newPassword: string, oldPassword?: string) => Promise<boolean>
  isLoading: boolean
  sessionToken?: string
  mode?: 'first-login' | 'change-password'
}

interface PasswordFormData {
  oldPassword?: string
  newPassword: string
  confirmPassword: string
}

export default function PasswordChangeForm({ 
  onPasswordChange, 
  isLoading, 
  sessionToken,
  mode = 'first-login'
}: PasswordChangeFormProps) {
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false
  })
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  
  const { register, handleSubmit, formState: { errors }, watch } = useForm<PasswordFormData>()
  const newPassword = watch('newPassword')
  
  const isFirstLogin = mode === 'first-login'

  // Password requirements based on the authentication flow requirements (3.3, 8.3)
  const passwordRequirements = [
    { test: (pwd: string) => pwd.length >= 8, text: 'At least 8 characters' },
    { test: (pwd: string) => /[A-Z]/.test(pwd), text: 'One uppercase letter' },
    { test: (pwd: string) => /[a-z]/.test(pwd), text: 'One lowercase letter' },
    { test: (pwd: string) => /\d/.test(pwd), text: 'One number' },
    { test: (pwd: string) => /[!@#$%^&*(),.?":{}|<>]/.test(pwd), text: 'One special character (!@#$%^&*)' }
  ]

  const handlePasswordChange = async (data: PasswordFormData) => {
    setMessage(null)
    
    // Validate old password is provided when changing password
    if (!isFirstLogin && !data.oldPassword) {
      setMessage({
        type: 'error',
        text: 'Current password is required'
      })
      return
    }

    // Validate passwords match (requirement 3.4)
    if (data.newPassword !== data.confirmPassword) {
      setMessage({
        type: 'error',
        text: 'New passwords do not match'
      })
      return
    }

    // Validate password meets all requirements (requirement 3.3)
    const failedRequirements = passwordRequirements.filter(req => !req.test(data.newPassword))
    if (failedRequirements.length > 0) {
      setMessage({
        type: 'error',
        text: 'Password must meet all security requirements'
      })
      return
    }

    const success = await onPasswordChange(data.newPassword, data.oldPassword)
    
    if (success) {
      setMessage({
        type: 'success',
        text: isFirstLogin 
          ? 'Password set successfully! You are now logged in.'
          : 'Password changed successfully! You can now use your new password to login.'
      })
    } else {
      setMessage({
        type: 'error',
        text: isFirstLogin
          ? 'Failed to set password. Please try again.'
          : 'Failed to change password. Please check your current password and try again.'
      })
    }
  }

  const togglePasswordVisibility = (field: 'new' | 'confirm') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }))
  }

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6">
      <div className="text-center mb-6">
        <Lock className="h-12 w-12 text-fwo-blue mx-auto mb-4" />
        <h2 className="text-2xl font-semibold text-gray-900">
          {isFirstLogin ? 'Set Your Password' : 'Change Password'}
        </h2>
        <p className="text-sm text-gray-600 mt-2">
          {isFirstLogin 
            ? 'Please create a secure password for your account'
            : 'Update your password to keep your account secure'
          }
        </p>
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded-md flex items-start ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
          ) : (
            <AlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
          )}
          <p className="text-sm">{message.text}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(handlePasswordChange)} className="space-y-4">

        {/* Current Password field - only shown when changing password (not first login) */}
        {!isFirstLogin && (
          <div>
            <label htmlFor="oldPassword" className="block text-sm font-medium text-gray-700 mb-2">
              Current Password
            </label>
            <div className="relative">
              <input
                {...register('oldPassword', {
                  required: !isFirstLogin ? 'Current password is required' : false
                })}
                type="password"
                id="oldPassword"
                className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-fwo-blue focus:border-fwo-blue"
                placeholder="Enter your current password"
              />
            </div>
            {errors.oldPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.oldPassword.message}</p>
            )}
          </div>
        )}

        <div>
          <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
            {isFirstLogin ? 'Create Password' : 'New Password'}
          </label>
          <div className="relative">
            <input
              {...register('newPassword', {
                required: 'Password is required',
                validate: (value) => {
                  if (!value) return 'Password is required'
                  
                  // Validate each requirement individually for better error messages
                  if (value.length < 8) {
                    return 'Password must be at least 8 characters long'
                  }
                  if (!/[A-Z]/.test(value)) {
                    return 'Password must contain at least one uppercase letter'
                  }
                  if (!/[a-z]/.test(value)) {
                    return 'Password must contain at least one lowercase letter'
                  }
                  if (!/\d/.test(value)) {
                    return 'Password must contain at least one number'
                  }
                  if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
                    return 'Password must contain at least one special character'
                  }
                  
                  return true
                }
              })}
              type={showPasswords.new ? 'text' : 'password'}
              id="newPassword"
              className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-fwo-blue focus:border-fwo-blue"
              placeholder={isFirstLogin ? 'Create a secure password' : 'Enter new password'}
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('new')}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
            >
              {showPasswords.new ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {errors.newPassword && (
            <p className="mt-1 text-sm text-red-600">{errors.newPassword.message}</p>
          )}
        </div>

        {/* Password Strength Indicator - Shows requirements and validation status */}
        <div className="bg-gray-50 p-3 rounded-md">
          <p className="text-sm font-medium text-gray-700 mb-2">Password Requirements:</p>
          <ul className="space-y-1">
            {passwordRequirements.map((req, index) => {
              const isValid = newPassword ? req.test(newPassword) : false
              return (
                <li key={index} className="flex items-center text-xs">
                  {isValid ? (
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  ) : (
                    <div className="h-4 w-4 border border-gray-300 rounded-full mr-2 flex-shrink-0" />
                  )}
                  <span className={isValid ? 'text-green-700' : 'text-gray-600'}>
                    {req.text}
                  </span>
                </li>
              )
            })}
          </ul>
          
          {/* Overall strength indicator */}
          {newPassword && (
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">Password Strength:</span>
                <span className={`font-medium ${
                  passwordRequirements.every(req => req.test(newPassword))
                    ? 'text-green-600'
                    : passwordRequirements.filter(req => req.test(newPassword)).length >= 3
                    ? 'text-yellow-600'
                    : 'text-red-600'
                }`}>
                  {passwordRequirements.every(req => req.test(newPassword))
                    ? 'Strong'
                    : passwordRequirements.filter(req => req.test(newPassword)).length >= 3
                    ? 'Medium'
                    : 'Weak'
                  }
                </span>
              </div>
              <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${
                    passwordRequirements.every(req => req.test(newPassword))
                      ? 'bg-green-500 w-full'
                      : passwordRequirements.filter(req => req.test(newPassword)).length >= 3
                      ? 'bg-yellow-500 w-2/3'
                      : 'bg-red-500 w-1/3'
                  }`}
                />
              </div>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
            Confirm Password
          </label>
          <div className="relative">
            <input
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (value) => {
                  const newPasswordValue = watch('newPassword')
                  if (value !== newPasswordValue) {
                    return 'Passwords do not match'
                  }
                  return true
                }
              })}
              type={showPasswords.confirm ? 'text' : 'password'}
              id="confirmPassword"
              className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-fwo-blue focus:border-fwo-blue"
              placeholder="Confirm your password"
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('confirm')}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
            >
              {showPasswords.confirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
          )}
          
          {/* Password match indicator */}
          {watch('confirmPassword') && watch('newPassword') && (
            <div className="mt-1 flex items-center text-xs">
              {watch('confirmPassword') === watch('newPassword') ? (
                <>
                  <CheckCircle className="h-3 w-3 text-green-500 mr-1" />
                  <span className="text-green-600">Passwords match</span>
                </>
              ) : (
                <>
                  <AlertCircle className="h-3 w-3 text-red-500 mr-1" />
                  <span className="text-red-600">Passwords do not match</span>
                </>
              )}
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading || !newPassword || !watch('confirmPassword') || newPassword !== watch('confirmPassword') || !passwordRequirements.every(req => req.test(newPassword || ''))}
          className="w-full bg-fwo-blue text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-fwo-blue focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
        >
          {isLoading ? (
            <>
              <Loader2 className="animate-spin h-4 w-4 mr-2" />
              {isFirstLogin ? 'Setting Password...' : 'Updating Password...'}
            </>
          ) : (
            isFirstLogin ? 'Set Password' : 'Update Password'
          )}
        </button>
      </form>

      <div className="mt-6 text-xs text-gray-500 text-center">
        <p>
          {isFirstLogin 
            ? 'This password will be used for all future logins to the portal.'
            : 'Your new password will be used for all future logins to the portal.'
          }
        </p>
        <p className="mt-1">Keep it secure and don't share it with others.</p>
        {sessionToken && (
          <p className="mt-1 text-gray-400">Session secured with temporary authentication token.</p>
        )}
      </div>
    </div>
  )
}